import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Wrapper from '../../components/Wrapper';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  CircularProgress,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { shortFunction, menu } from '../../translate';
import { greenTheme } from '../../helpers/style';
import Api from '../../Api';
import { toast } from 'react-toastify';
import { allPermissions, splitStoredPermissions, packPermissions } from './EmployeePermissions';

const rowToRole = (row) => {
  if (!row) return null;
  const stored = splitStoredPermissions(row.permissions_json);
  return {
    id: row.role_key,
    dbId: row.id,
    name: ['', row.name_en || '', row.name_ru || '', row.name_hy || ''],
    color: row.color || '#7ebb08',
    isDefault: !!row.is_default,
    permissions: stored.permissions,
    groupHistoryIds: stored.groupHistoryIds,
  };
};

const primaryColor = greenTheme.primary;
const primaryHover = greenTheme.hover || '#6aa309';

const style = {
  bgcolor: 'background.paper',
  p: 4,
  overflow: 'auto',
  borderRadius: 3,
};

export default function RoleViewPage() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const langIndex = +localStorage.getItem('atsLang') || 1;

  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editName, setEditName] = useState({ en: '', ru: '', hy: '' });
  const [editColor, setEditColor] = useState('#7ebb08');
  const [groups, setGroups] = useState([]);
  const userData = useSelector((state) => state.users.userData);
  const canPickGroups = Number(userData?.id) === 10 || Number(userData?.id) === 3888;

  const isEditMode = searchParams.get('edit') === '1';
  const canEdit = !!role;

  useEffect(() => {
    if (!roleId) return;
    (async () => {
      setLoading(true);
      try {
        const resp = await Api.rolesGet(roleId);
        const row = resp?.data?.data;
        setRole(row ? rowToRole(row) : null);
      } catch (e) {
        setRole(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [roleId]);

  useEffect(() => {
    if (!canPickGroups) return;
    (async () => {
      try {
        const resp = await Api.groupsGet();
        setGroups(Array.isArray(resp?.data?.data) ? resp.data.data : []);
      } catch (e) {
        setGroups([]);
      }
    })();
  }, [canPickGroups]);

  const handlePermissionToggle = (permissionId) => {
    if (!role) return;
    const perms = role.permissions || [];
    const has = perms.includes(permissionId);
    let next = has
      ? perms.filter((p) => p !== permissionId)
      : [...perms, permissionId];
    if (!has && ['call_history_own', 'call_history_all', 'group_history_view', 'group_audio_view'].includes(permissionId)) {
      next = [...next, 'history_view'];
    }
    if (permissionId === 'group_audio_view' && !has) {
      next = [...next, 'group_history_view'];
    }
    setRole((prev) => (prev ? { ...prev, permissions: [...new Set(next)] } : null));
  };

  const handleOpenEdit = () => {
    if (!role) return;
    setEditName({
      en: role.name?.[1] || '',
      ru: role.name?.[2] || '',
      hy: role.name?.[3] || '',
    });
    setEditColor(role.color || '#7ebb08');
    setOpenEditDialog(true);
  };

  const handleSaveEdit = () => {
    if (!editName.en.trim()) {
      toast.error(
        langIndex === 1 ? 'Role name is required' :
        langIndex === 2 ? 'Название роли обязательно' :
        'Դերի անունը պարտադիր է'
      );
      return;
    }
    setRole((prev) => (prev ? {
      ...prev,
      name: ['', editName.en, editName.ru, editName.hy],
      color: editColor,
    } : null));
    setOpenEditDialog(false);
    toast.success(langIndex === 2 ? 'Роль обновлена' : langIndex === 3 ? 'Դերը թարմացվեց' : 'Role updated');
  };

  const handleSave = async () => {
    if (!role?.dbId) {
      toast.error(langIndex === 2 ? 'Роль не найдена' : langIndex === 3 ? 'Դերը չի գտնվել' : 'Role not found');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name_en: role.name?.[1] || '',
        name_ru: role.name?.[2] || '',
        name_hy: role.name?.[3] || '',
        color: role.color || null,
        is_default: role.isDefault ? 1 : 0,
        permissions: packPermissions(role.permissions, canPickGroups ? role.groupHistoryIds : []),
      };
      await Api.rolesUpdate(role.dbId, payload);
      toast.success(langIndex === 2 ? 'Сохранено' : langIndex === 3 ? 'Պահպանվեց' : 'Saved');
    } catch (e) {
      console.error(e);
      toast.error(langIndex === 2 ? 'Не удалось сохранить' : langIndex === 3 ? 'Չհաջողվեց պահպանել' : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!role || role.isDefault) {
      toast.error(
        langIndex === 1 ? 'Cannot delete default role' :
        langIndex === 2 ? 'Нельзя удалить роль по умолчанию' :
        'Հնարավոր չէ ջնջել լռելյայն դերը'
      );
      return;
    }
    if (!window.confirm(langIndex === 2 ? 'Удалить роль?' : langIndex === 3 ? 'Ջնջե՞լ դերը' : 'Delete role?')) return;
    try {
      await Api.rolesDelete(role.dbId ?? role.id);
      toast.success(langIndex === 2 ? 'Роль удалена' : langIndex === 3 ? 'Դերը ջնջվեց' : 'Role deleted');
      navigate('/short_function/employees/permissions');
    } catch (e) {
      toast.error(langIndex === 2 ? 'Не удалось удалить' : langIndex === 3 ? 'Չհաջողվեց ջնջել' : 'Delete failed');
    }
  };

  if (loading) {
    return (
      <Wrapper>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', gap: 2 }}>
          <CircularProgress sx={{ color: primaryColor }} />
          <Typography>{langIndex === 2 ? 'Загрузка...' : langIndex === 3 ? 'Բեռնվում է...' : 'Loading...'}</Typography>
        </Box>
      </Wrapper>
    );
  }

  if (!role) {
    return (
      <Wrapper>
        <Paper sx={style}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {langIndex === 2 ? 'Роль не найдена' : langIndex === 3 ? 'Դերը չի գտնվել' : 'Role not found'}
          </Typography>
          <Button onClick={() => navigate('/short_function/employees/permissions')}>
            {langIndex === 2 ? 'Назад' : langIndex === 3 ? 'Հետ' : 'Back'}
          </Button>
        </Paper>
      </Wrapper>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {role.name?.[langIndex] || role.id} - {shortFunction.employee_permissions?.[langIndex] || 'Permissions'} - {menu.worker?.[langIndex] || ''}
        </title>
      </Helmet>
      <Wrapper>
        <div className="tableParent">
          <Paper sx={style}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3, pb: 2.5, borderBottom: '2px solid rgba(0, 0, 0, 0.08)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  onClick={() => navigate('/short_function/employees/permissions')}
                  sx={{ minWidth: 'auto', p: 1, color: 'inherit', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
                >
                  <ArrowBackIosNewIcon />
                </Button>
                <Chip
                  label={role.name?.[langIndex] || role.id}
                  sx={{
                    color: 'black',
                    backgroundColor: 'transparent',
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {!isEditMode ? (
                  canEdit && (
                    <Button
                      variant="outlined"
                      onClick={() => navigate(`/short_function/employees/permissions/role/${roleId}?edit=1`)}
                      sx={{ borderColor: primaryColor, color: primaryColor }}
                    >
                      {langIndex === 2 ? 'Редактировать' : langIndex === 3 ? 'Խմբագրել' : 'Edit'}
                    </Button>
                  )
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleOpenEdit}
                      sx={{ borderColor: primaryColor, color: primaryColor }}
                    >
                      {langIndex === 2 ? 'Редактировать роль' : langIndex === 3 ? 'Խմբագրել դերը' : 'Edit role'}
                    </Button>
                    {!role.isDefault && (
                      <Button
                        variant="outlined"
                        onClick={handleDelete}
                        sx={{ borderColor: '#ef4444', color: '#ef4444' }}
                      >
                        {langIndex === 2 ? 'Удалить роль' : langIndex === 3 ? 'Ջնջել դերը' : 'Delete role'}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={saving}
                      sx={{ backgroundColor: primaryColor, '&:hover': { backgroundColor: primaryHover } }}
                    >
                      {saving
                        ? (langIndex === 2 ? 'Сохранение...' : langIndex === 3 ? 'Պահպանվում է...' : 'Saving...')
                        : (langIndex === 2 ? 'Сохранить' : langIndex === 3 ? 'Պահպանել' : 'Save')}
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {langIndex === 2 ? 'Разрешения' : langIndex === 3 ? 'Թույլտվություններ' : 'Permissions'}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {allPermissions.map((permission) => {
                  const hasPermission = (role.permissions || []).includes(permission.id);
                  const showGroupPicker = permission.id === 'group_history_view' && hasPermission && canPickGroups;
                  const selectedGroups = groups.filter((g) => (role.groupHistoryIds || []).includes(Number(g.id)));
                  return (
                    <Box key={permission.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          checked={!!hasPermission}
                          onChange={() => handlePermissionToggle(permission.id)}
                          disabled={!isEditMode}
                          sx={{ color: role.color, '&.Mui-checked': { color: role.color } }}
                        />
                        <Typography variant="body2">
                          {permission.name?.[langIndex] || permission.id}
                        </Typography>
                      </Box>
                      {showGroupPicker && (
                        <Autocomplete
                          multiple
                          size="small"
                          options={groups}
                          value={selectedGroups}
                          disabled={!isEditMode}
                          getOptionLabel={(option) => `${option.name || ''} (${option.virtual || option.id})`}
                          isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                          onChange={(_, value) => {
                            setRole((prev) => (prev ? {
                              ...prev,
                              groupHistoryIds: value.map((g) => Number(g.id)),
                            } : null));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={langIndex === 2 ? 'Группы' : langIndex === 3 ? 'Խմբեր' : 'Groups'}
                            />
                          )}
                          sx={{ ml: 5, mb: 1, maxWidth: 480 }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Paper>
        </div>
      </Wrapper>

      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {langIndex === 2 ? 'Редактировать роль' : langIndex === 3 ? 'Խմբագրել դերը' : 'Edit role'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label={langIndex === 2 ? 'Название (англ.)' : langIndex === 3 ? 'Անուն (անգլերեն)' : 'Name (English)'}
              value={editName.en}
              onChange={(e) => setEditName((p) => ({ ...p, en: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label={langIndex === 2 ? 'Название (рус.)' : langIndex === 3 ? 'Անուն (ռուսերեն)' : 'Name (Russian)'}
              value={editName.ru}
              onChange={(e) => setEditName((p) => ({ ...p, ru: e.target.value }))}
              fullWidth
            />
            <TextField
              label={langIndex === 2 ? 'Название (арм.)' : langIndex === 3 ? 'Անուն (հայերեն)' : 'Name (Armenian)'}
              value={editName.hy}
              onChange={(e) => setEditName((p) => ({ ...p, hy: e.target.value }))}
              fullWidth
            />
            <TextField
              label={langIndex === 2 ? 'Цвет' : langIndex === 3 ? 'Գույն' : 'Color'}
              type="color"
              value={editColor}
              onChange={(e) => setEditColor(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>
            {langIndex === 2 ? 'Отмена' : langIndex === 3 ? 'Չեղարկել' : 'Cancel'}
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" sx={{ backgroundColor: primaryColor }}>
            {langIndex === 2 ? 'Сохранить' : langIndex === 3 ? 'Պահպանել' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
